
import React, { useState } from "react";
import { Eye, Home, Star, ShoppingCart, Send } from 'lucide-react';

const RightPanelDesktop = ({
  handleCheckout, handleSaveEmail, email, setEmail, vendorColor, GoToRoomSelection, hideStagedSofa, setHideStagedSofa,
  setShowBoundingBox, hasStagedImage, ratingArgs, usingInteractiveSofaComponent}) => {
    return (
      <div>
        <div
          className="rounded-xl text-center text-white cursor-pointer hover:shadow-lg"
          style={{ backgroundColor: vendorColor }}
        >
          <button
            className="w-full py-2 font-semibold flex items-center justify-center space-x-2"
            onClick={handleCheckout}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Go to Product Page</span>
          </button>
        </div>
  
        <div className="mt-4">
          <EmailForm
            handleSaveEmail={handleSaveEmail}
            email={email}
            setEmail={setEmail}
            vendorColor={vendorColor}/>
        </div>
  
        <div className="py-2 mt-2">
        <EngagementBar 
          GoToRoomSelection={GoToRoomSelection} 
          vendorColor={vendorColor} 
          hideStagedSofa={hideStagedSofa} 
          setHideStagedSofa={setHideStagedSofa} 
          setShowBoundingBox={setShowBoundingBox} 
          hasStagedImage={hasStagedImage} 
          ratingArgs={ratingArgs} 
          isMobile={false}
          usingInteractiveSofaComponent={usingInteractiveSofaComponent}
        />
        </div>
      </div>
    );
  };
  

  function EmailForm({handleSaveEmail, email, setEmail, vendorColor}) {
    const [error, setError] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const validateEmail = (email) => {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      return emailRegex.test(email);
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsTyping(false);
      
      if (!email.trim()) {
        setError('Email is required');
        return;
      }
      
      if (!validateEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }
  
      setError('');
      setIsSubmitting(true);
      try {
        await handleSaveEmail(e);
      } catch (error) {
        setError('Failed to save email. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };
  
    const handleChange = (e) => {
      setEmail(e.target.value);
      setIsTyping(true);
      if (error && validateEmail(e.target.value)) {
        setError('');
      }
    };
  
    const handleBlur = () => {
      setIsTyping(false);
      if (email && !validateEmail(email)) {
        setError('Please enter a valid email address');
      }
    };
  
    return (
      <div className="w-full">
        <h1 className="text-[16px] font-bold text-[#2D3033] mb-2">
          Get inspiration from our designers
        </h1>
        <form onSubmit={handleSubmit} className="flex gap-4">
          <div className="flex-1">
            <input
              type="email"
              name="customer-email"
              id="customer-email"
              placeholder="Your Email"
              className={`w-full rounded-lg border ${
                error && !isTyping ? 'border-red-500' : 'border-[#E3E4E5]'
              } bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
              value={email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {error && !isTyping && (
              <p className="mt-1 text-xs text-red-500">{error}</p>
            )}
          </div>
          <button
            type="submit"
            style={{ backgroundColor: vendorColor }}
            className="h-10 w-10 rounded-lg flex items-center justify-center text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Submit Email"
            disabled={!!error || !email || isSubmitting}
          >
            <Send className={`w-5 h-5 ${isSubmitting ? 'animate-pulse' : ''}`} />
          </button>
        </form>
      </div>
    );
  }

const buttonBaseClasses = "w-full h-12 px-4 rounded-lg flex items-center justify-center gap-2 transition-all hover:shadow-lg";

function RatingButton({ rating, handleRatingDropdownClick, handleRating, showRatingDropdown }, vendorColor, isMobile) {
  return (
    <button
      style={{ backgroundColor: vendorColor }}
      className={`${buttonBaseClasses} text-white`}
      onClick={handleRatingDropdownClick}
      title="Rate Your Experience"
    >
      <Star className="w-5 h-5" />
      {!isMobile && <span>Rate Your Experience</span>}
      {showRatingDropdown && (
        <div className="absolute z-10 bg-white shadow-lg rounded-md p-4 mt-2 right-0">
          <h3 className="text-lg font-medium mb-2 text-gray-800">Rate your experience</h3>
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                className={`p-2 rounded-full transition-colors ${
                  star <= rating
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                }`}
                onClick={() => handleRating(star)}
              >
                <Star size={24} />
              </button>
            ))}
          </div>
        </div>
      )}
    </button>
  );
}

function EngagementBar({
  GoToRoomSelection,
  vendorColor,
  hideStagedSofa,
  setHideStagedSofa,
  setShowBoundingBox,
  hasStagedImage,
  ratingArgs,
  isMobile,
  usingInteractiveSofaComponent, 
}) {
  const containerClasses = isMobile
    ? "flex flex-row gap-4 w-full"
    : "flex flex-col gap-4 w-full";

  return (
    <div className="w-full max-w-[1000px] mt-2">
      <div className={containerClasses}>
        {/* Toggle Original Image Button */}
        {!usingInteractiveSofaComponent && <button
          style={{
            backgroundColor: hasStagedImage ? vendorColor : "white",
            borderWidth: !hasStagedImage ? "1px" : "0",
            borderColor: "rgb(156 163 175)",
          }}
          className={`${buttonBaseClasses} ${
            hasStagedImage ? "text-white" : "text-gray-400"
          } disabled:cursor-not-allowed`}
          onClick={() => {
            if (hasStagedImage) {
              setHideStagedSofa(!hideStagedSofa);
              setShowBoundingBox(false);
            }
          }}
          disabled={!hasStagedImage}
          title="Toggle Original Image"
        >
          <Eye className="w-5 h-5" />
          {!isMobile && <span>Toggle Original</span>}
        </button>}


        {/* Change Room Button */}
        <button
          style={{ backgroundColor: vendorColor }}
          className={`${buttonBaseClasses} text-white`}
          onClick={GoToRoomSelection}
          title="Change Room"
        >
          <Home className="w-5 h-5" />
          {!isMobile && <span>Change Room</span>}
        </button>

        {/* Rating Button */}
        {RatingButton(ratingArgs, vendorColor, isMobile)}
      </div>
    </div>
  );
}


export default { ControlPaneComponent: RightPanelDesktop, EngagementBar, buttonBaseClasses,EmailForm };

